import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, ArrowRight, X, RefreshCw, Heart, Share2, Download } from 'lucide-react';
import { buildVibeShareImage } from '@/lib/vibeShareImage';

/**
 * VibeQuiz — 5-question personality quiz that assigns a Reader Vibe archetype
 * and shows 3 curated BookTok recommendations. Saves result to user.reader_vibe.
 */

const QUESTIONS = [
  {
    id: 'setting',
    prompt: 'What world do you want to fall into tonight?',
    choices: [
      { label: 'A cozy magical village with talking plants', vibe: 'cozy' },
      { label: 'A dark academia library with hidden secrets', vibe: 'slow' },
      { label: 'A battlefield with dragons overhead', vibe: 'epic' },
      { label: 'A castle where the villain kidnaps you', vibe: 'dark' },
    ],
  },
  {
    id: 'spice',
    prompt: 'Your ideal spice level? 🌶️',
    choices: [
      { label: 'Sweet & swoony, closed door', vibe: 'cozy' },
      { label: 'Some heat, but plot first', vibe: 'slow' },
      { label: "Steamy — let's go", vibe: 'epic' },
      { label: 'Set my ereader on fire', vibe: 'dark' },
    ],
  },
  {
    id: 'trope',
    prompt: 'Pick your favorite trope',
    choices: [
      { label: 'Found family & soft feelings', vibe: 'cozy' },
      { label: 'Slow burn academic rivals', vibe: 'slow' },
      { label: 'Enemies to lovers with SWORDS', vibe: 'epic' },
      { label: 'Morally-grey villain LI', vibe: 'dark' },
    ],
  },
  {
    id: 'pace',
    prompt: 'How do you like your pacing?',
    choices: [
      { label: 'A gentle stroll through the forest', vibe: 'cozy' },
      { label: 'Character studies over coffee', vibe: 'slow' },
      { label: 'Full sprint with battle scenes', vibe: 'epic' },
      { label: 'Twisty & tense, cliffhangers welcome', vibe: 'dark' },
    ],
  },
  {
    id: 'ending',
    prompt: 'How does the perfect ending feel?',
    choices: [
      { label: 'A warm hug — everyone is safe', vibe: 'cozy' },
      { label: 'Bittersweet & poetic', vibe: 'slow' },
      { label: 'Epic sacrifice, sequel promised', vibe: 'epic' },
      { label: 'A gasp — nothing is as it seems', vibe: 'dark' },
    ],
  },
];

const VIBES = {
  cozy: {
    name: 'Cozy Bestie',
    emoji: '🌿',
    color: '#708C6E',
    tagline: 'You read for the warmth, the found family, and the healing.',
    picks: [
      { title: 'Divine Rivals', author: 'Rebecca Ross', why: 'Rivals-to-lovers with poetic slow-burn and gentle magic.' },
      { title: 'The Very Secret Society of Irregular Witches', author: 'Sangu Mandanna', why: 'Cozy witches + found family + a small English village.' },
      { title: 'Sorcery of Thorns', author: 'Margaret Rogerson', why: 'Magical library, sweet romance, no darkness overload.' },
    ],
  },
  slow: {
    name: 'Slow-Burn Scholar',
    emoji: '📖',
    color: '#7A3B4A',
    tagline: 'You savor prose, character depth, and the ache of a slow reveal.',
    picks: [
      { title: 'One Dark Window', author: 'Rachel Gillig', why: 'Gothic atmosphere with slow-burn tension and lush prose.' },
      { title: 'The Night Circus', author: 'Erin Morgenstern', why: 'Atmospheric, dreamlike, character-driven magic.' },
      { title: 'Divine Rivals', author: 'Rebecca Ross', why: 'Epistolary slow-burn — you\'ll live in the letters.' },
    ],
  },
  epic: {
    name: 'Dragon Rider',
    emoji: '🐉',
    color: '#6B7A4F',
    tagline: 'You want dragons, swords, spice, and no chapter without stakes.',
    picks: [
      { title: 'Onyx Storm', author: 'Rebecca Yarros', why: 'The Empyrean trilogy — dragons, war college, spice, banter.' },
      { title: 'A Fate Inked in Blood', author: 'Danielle L. Jensen', why: 'Norse fantasy with fated-mates energy and epic battles.' },
      { title: 'Iron Flame', author: 'Rebecca Yarros', why: 'If you loved Fourth Wing, this ups everything.' },
    ],
  },
  dark: {
    name: 'Dark Romantic',
    emoji: '🔥',
    color: '#3B0A15',
    tagline: 'You crave the villain, the danger, and the delicious moral grey.',
    picks: [
      { title: 'The Serpent & the Wings of Night', author: 'Carissa Broadbent', why: 'Vampire enemies-to-lovers with brutal beauty.' },
      { title: 'Quicksilver', author: 'Callie Hart', why: 'Fae, morally-grey love interest, obsession, spice.' },
      { title: 'Phantasma', author: 'Kaylie Smith', why: 'Dark magical challenges + slow-burn dread + spice.' },
    ],
  },
};

const VibeQuiz = ({ onClose, onComplete, currentVibe, firstName }) => {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [step, setStep] = useState(currentVibe ? 'result' : 0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(currentVibe || null);
  const [saving, setSaving] = useState(false);

  const total = QUESTIONS.length;

  const selectChoice = (choice) => {
    const q = QUESTIONS[step];
    const next = { ...answers, [q.id]: choice.vibe };
    setAnswers(next);
    if (step + 1 < total) {
      setStep(step + 1);
    } else {
      // Compute winner
      const tally = { cozy: 0, slow: 0, epic: 0, dark: 0 };
      Object.values(next).forEach((v) => { tally[v] += 1; });
      const winner = Object.entries(tally).sort((a, b) => b[1] - a[1])[0][0];
      setResult(winner);
      setStep('result');
      saveVibe(winner);
    }
  };

  const saveVibe = async (vibeKey) => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/api/profile`,
        { reader_vibe: vibeKey },
        { withCredentials: true }
      );
      if (onComplete) onComplete(vibeKey);
    } catch (e) {
      toast.error('Could not save your vibe — you can retake anytime.');
    } finally {
      setSaving(false);
    }
  };

  const retake = () => {
    setAnswers({});
    setResult(null);
    setStep(0);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      data-testid="vibe-quiz-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative bg-[#FDFBF7] rounded-3xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto"
        style={{ border: '1px solid #E1D9CD' }}
      >
        {/* Close */}
        <button
          onClick={onClose}
          data-testid="vibe-quiz-close"
          aria-label="Close vibe quiz"
          className="absolute top-4 right-4 z-10 p-2 rounded-full hover:bg-[#E2E6D5]/50 transition-colors"
        >
          <X className="w-5 h-5 text-[#5C5955]" />
        </button>

        {step === 'result' && result ? (
          <ResultView
            vibe={VIBES[result]}
            vibeKey={result}
            firstName={firstName}
            onRetake={retake}
            onClose={onClose}
            saving={saving}
          />
        ) : (
          <QuestionView
            step={step}
            total={total}
            question={QUESTIONS[step]}
            onSelect={selectChoice}
          />
        )}
      </div>
    </div>
  );
};

const QuestionView = ({ step, total, question, onSelect }) => (
  <div className="p-8 sm:p-10">
    {/* Header */}
    <div className="flex items-center gap-3 mb-2">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#6B7A4F] to-[#434E30] flex items-center justify-center shadow-md">
        <Sparkles className="w-5 h-5 text-white" strokeWidth={2} />
      </div>
      <div>
        <p className="text-xs tracking-[0.2em] uppercase text-[#6B7A4F] font-bold">Reader Vibe Quiz</p>
        <p className="text-xs text-[#5C5955]">Question {step + 1} of {total}</p>
      </div>
    </div>
    {/* Progress bar */}
    <div className="w-full h-1.5 bg-[#E2E6D5] rounded-full mt-4 mb-6 overflow-hidden">
      <div
        className="h-full bg-gradient-to-r from-[#6B7A4F] to-[#434E30] transition-all duration-500 rounded-full"
        style={{ width: `${((step + 1) / total) * 100}%` }}
      />
    </div>

    <h2 className="text-2xl sm:text-3xl font-light text-[#2D2A26] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
      {question.prompt}
    </h2>

    <div className="space-y-3">
      {question.choices.map((choice, i) => (
        <button
          key={i}
          onClick={() => onSelect(choice)}
          data-testid={`vibe-quiz-choice-${question.id}-${i}`}
          className="w-full text-left p-4 sm:p-5 rounded-2xl bg-white/60 border border-[#E1D9CD] hover:border-[#6B7A4F] hover:bg-[#E2E6D5]/40 transition-all duration-200 flex items-center justify-between group"
        >
          <span className="text-[#2D2A26] text-base sm:text-lg">{choice.label}</span>
          <ArrowRight className="w-4 h-4 text-[#5C5955] opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
        </button>
      ))}
    </div>
  </div>
);

const ResultView = ({ vibe, vibeKey, firstName, onRetake, onClose, saving }) => {
  const [sharing, setSharing] = useState(false);

  const canNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  const generateAndAct = async (mode) => {
    setSharing(true);
    try {
      const blob = await buildVibeShareImage({ vibeKey, vibe, firstName });
      if (!blob) throw new Error('image build failed');
      const file = new File([blob], 'my-reader-vibe.png', { type: 'image/png' });

      if (mode === 'share' && canNativeShare) {
        // Try file share first, fall back to text share
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: `I'm A ${vibe.name}`,
            text: `${vibe.tagline} — find your Reader Vibe at bookishbestie.com`,
          });
        } else {
          await navigator.share({
            title: `I'm A ${vibe.name}`,
            text: `${vibe.tagline} — find your Reader Vibe at bookishbestie.com`,
            url: 'https://bookishbestie.com',
          });
        }
      } else {
        // Download
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bookish-bestie-${vibeKey}-vibe.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Vibe image saved to your device 📸');
      }
    } catch (e) {
      if (e && e.name !== 'AbortError') {
        toast.error('Could not create image — please try again.');
      }
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="p-8 sm:p-10 text-center" data-testid={`vibe-result-${vibeKey}`}>
      <div
        className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 shadow-lg"
        style={{ background: `linear-gradient(135deg, ${vibe.color}, ${vibe.color}CC)` }}
      >
        <span className="text-4xl">{vibe.emoji}</span>
      </div>
      <p className="text-xs tracking-[0.2em] uppercase text-[#6B7A4F] font-bold">You are</p>
      <h2
        className="text-4xl sm:text-5xl font-bold mt-1 mb-3"
        style={{ fontFamily: 'Playfair Display, serif', color: vibe.color }}
      >
        A {vibe.name}
      </h2>
      <p className="text-base sm:text-lg text-[#5C5955] max-w-lg mx-auto mb-8">
        {vibe.tagline}
      </p>

      <div className="text-left mb-6">
        <p className="text-xs tracking-[0.2em] uppercase text-[#6B7A4F] font-bold mb-3 text-center">
          Your Recommended Reads
        </p>
        <div className="space-y-3">
          {vibe.picks.map((p, i) => (
            <div
              key={i}
              data-testid={`vibe-rec-${i}`}
              className="flex items-start gap-3 p-4 rounded-2xl bg-white/60 border border-[#E1D9CD]"
            >
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: '#E2E6D5' }}
              >
                <Heart className="w-4 h-4" style={{ color: vibe.color }} strokeWidth={2.5} fill={vibe.color} />
              </div>
              <div>
                <p className="font-semibold text-[#2D2A26]">{p.title}</p>
                <p className="text-xs text-[#5C5955] mb-1">by {p.author}</p>
                <p className="text-sm text-[#5C5955] italic">{p.why}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Share row */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center mb-3">
        {canNativeShare && (
          <button
            onClick={() => generateAndAct('share')}
            disabled={sharing}
            data-testid="vibe-share-btn"
            className="px-6 py-3 rounded-full text-white font-semibold shadow-md flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ background: `linear-gradient(135deg, ${vibe.color}, ${vibe.color}CC)` }}
          >
            <Share2 className="w-4 h-4" />
            {sharing ? 'Creating…' : 'Share My Vibe'}
          </button>
        )}
        <button
          onClick={() => generateAndAct('download')}
          disabled={sharing}
          data-testid="vibe-download-btn"
          className="px-6 py-3 rounded-full bg-white border border-[#6B7A4F] text-[#6B7A4F] font-semibold hover:bg-[#E2E6D5]/40 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <Download className="w-4 h-4" />
          {sharing ? 'Creating…' : 'Download Image'}
        </button>
      </div>

      {/* Secondary row */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button
          onClick={onRetake}
          data-testid="vibe-quiz-retake"
          className="px-6 py-3 rounded-full bg-transparent text-[#5C5955] font-medium hover:text-[#6B7A4F] transition-colors flex items-center justify-center gap-2 text-sm"
        >
          <RefreshCw className="w-4 h-4" />
          Retake Quiz
        </button>
        <button
          onClick={onClose}
          data-testid="vibe-quiz-done"
          className="px-6 py-3 rounded-full bg-[#6B7A4F] text-white font-semibold hover:bg-[#57653F] transition-colors shadow-md"
        >
          {saving ? 'Saving…' : 'Find My Bookish Bestie'}
        </button>
      </div>
    </div>
  );
};

// Expose the vibe metadata so other components (Dashboard) can show a saved-vibe badge
export const VIBE_META = VIBES;

export default VibeQuiz;
